// ===== FILE: src/hooks/useContractorLocations.ts =====
import { useState, useEffect } from 'react';
import { contractorAPI, LocationWithCount } from '../services/contractorService';

interface UseContractorLocationsReturn {
  locations: LocationWithCount[];
  loading: boolean;
  error: string | null;
  states: string[];
  citiesByState: Record<string, LocationWithCount[]>;
  refetch: () => void;
}

export const useContractorLocations = (): UseContractorLocationsReturn => {
  const [locations, setLocations] = useState<LocationWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLocations = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await contractorAPI.getLocationsWithCounts();
      setLocations(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  // Compute derived data
  const states = Array.from(new Set(locations.map(loc => loc.state))).sort();
  
  const citiesByState = locations.reduce((acc, location) => {
    if (location.city) {
      if (!acc[location.state]) {
        acc[location.state] = [];
      }
      acc[location.state].push(location);
    }
    return acc;
  }, {} as Record<string, LocationWithCount[]>);

  return {
    locations,
    loading,
    error,
    states,
    citiesByState,
    refetch: fetchLocations
  };
};